import type { DesertPalette } from '../../shared/full/palette';
import { nextFloat, seedRng } from '../../shared/prng';
import type { Vec2 } from '../../shared/vec';
import type { Arena } from '../../sim/state';
import { OVERFLOW } from '../ground-layout';

const TAU = Math.PI * 2;
const RUG_COUNT = 8;
const RUG_WIDTH = 92;
const RUG_LENGTHS = [150, 166, 182] as const;
const RUG_RINGS = [210, 240] as const;
const RUG_STRETCH = { x: 1.3, y: 0.92 } as const;
const RUG_TURN = 0.2;
const RUG_JITTER = (14 * Math.PI) / 180;
const RUG_BORDER = 18;
const RUG_DIAMOND_SPAN = 52;
const RIPPLE_SPACING = 36;
const RIPPLE_STEP = 30;
const RIPPLE_TILT = (-10 * Math.PI) / 180;
const DUNE_REACH = 0.4;
const DUNE_STEPS = 48;
const DUNES = [
  [-800, -330, -360, -500],
  [380, -500, 800, -250],
  [-800, 300, -380, 500],
  [420, 500, 800, 280],
] as const;

export type RugColor = keyof Omit<DesertPalette, 'tapis'>;

export interface Weave {
  readonly field: RugColor;
  readonly motif: RugColor;
  readonly border: RugColor;
}

export const WEAVES: readonly [Weave, ...Weave[]] = [
  { field: 'garance', motif: 'safran', border: 'indigo' },
  { field: 'indigo', motif: 'safran', border: 'garance' },
  { field: 'ocre', motif: 'creme', border: 'indigo' },
];

export interface Rug extends Vec2 {
  readonly angle: number;
  readonly width: number;
  readonly length: number;
  readonly weave: Weave;
}

export interface Diamond {
  readonly along: number;
  readonly halfWidth: number;
  readonly halfLength: number;
}

export function rugsOf(center: Vec2, seed: number): Rug[] {
  const rng = seedRng((seed + 9283) >>> 0);
  return Array.from({ length: RUG_COUNT }, (_, index) => {
    const turn = (index / RUG_COUNT) * TAU + RUG_TURN;
    const ring = RUG_RINGS[index % RUG_RINGS.length] ?? RUG_RINGS[0];
    return {
      x: center.x + ring * Math.cos(turn) * RUG_STRETCH.x,
      y: center.y + ring * Math.sin(turn) * RUG_STRETCH.y,
      angle: turn + Math.PI + (nextFloat(rng) - 0.5) * RUG_JITTER,
      width: RUG_WIDTH,
      length: RUG_LENGTHS[index % RUG_LENGTHS.length] ?? RUG_LENGTHS[0],
      weave: WEAVES[index % WEAVES.length] ?? WEAVES[0],
    };
  });
}

export function rugPoint(rug: Rug, across: number, along: number): Vec2 {
  const cos = Math.cos(rug.angle);
  const sin = Math.sin(rug.angle);
  return { x: rug.x + along * cos - across * sin, y: rug.y + along * sin + across * cos };
}

export function diamondsOf(rug: Rug): Diamond[] {
  const inner = rug.length - 2 * RUG_BORDER;
  const count = Math.max(2, Math.round(rug.length / RUG_DIAMOND_SPAN));
  return Array.from({ length: count }, (_, index) => ({
    along: -inner / 2 + (inner * (index + 0.5)) / count,
    halfWidth: rug.width / 2 - 16,
    halfLength: inner / count / 2 - 3,
  }));
}

export function khatam(center: Vec2, radius: number, rotation = 0): Vec2[] {
  const inner = (radius * Math.cos(Math.PI / 4)) / Math.cos(Math.PI / 8);
  return Array.from({ length: 16 }, (_, index) => {
    const angle = rotation + (index * Math.PI) / 8;
    const reach = index % 2 === 0 ? radius : inner;
    return { x: center.x + reach * Math.cos(angle), y: center.y + reach * Math.sin(angle) };
  });
}

// Run on past the arena so that a wide screen does not see them end.
export function dunesOf(arena: Arena): Vec2[][] {
  const cx = arena.width / 2;
  const cy = arena.height / 2;
  return DUNES.map(([x0, y0, x1, y1]) => {
    const control = {
      x: (x0 + x1) / 2 + (y0 < 0 ? 90 : -90) * Math.sign(x0 + x1),
      y: (y0 + y1) / 2 + (y0 < 0 ? 70 : -70),
    };
    return Array.from({ length: DUNE_STEPS + 1 }, (_, step) => {
      const t = -DUNE_REACH + ((1 + 2 * DUNE_REACH) * step) / DUNE_STEPS;
      const u = 1 - t;
      return {
        x: cx + u * u * x0 + 2 * u * t * control.x + t * t * x1,
        y: cy + u * u * y0 + 2 * u * t * control.y + t * t * y1,
      };
    });
  });
}

export function ripplesOf(arena: Arena, seed: number): Vec2[][] {
  const rng = seedRng((seed + 8117) >>> 0);
  const cx = arena.width / 2;
  const cy = arena.height / 2;
  const cos = Math.cos(RIPPLE_TILT);
  const sin = Math.sin(RIPPLE_TILT);
  const halfX = cx + OVERFLOW;
  const halfY = cy + OVERFLOW;
  const reachX = halfX * cos + halfY * Math.abs(sin) + RIPPLE_STEP;
  const reachY = halfX * Math.abs(sin) + halfY * cos + RIPPLE_SPACING;
  const rows = Math.ceil(reachY / RIPPLE_SPACING);
  const steps = Math.ceil(reachX / RIPPLE_STEP);
  const ripples: Vec2[][] = [];
  for (let row = -rows; row <= rows; row += 1) {
    const phase = nextFloat(rng) * TAU;
    const amplitude = 7 + nextFloat(rng) * 6;
    const ripple: Vec2[] = [];
    for (let step = -steps; step <= steps; step += 1) {
      const u = step * RIPPLE_STEP;
      const v =
        row * RIPPLE_SPACING +
        amplitude * Math.sin(u / 70 + phase) +
        3 * Math.sin(u / 23 + phase * 2);
      ripple.push({ x: cx + u * cos - v * sin, y: cy + u * sin + v * cos });
    }
    ripples.push(ripple);
  }
  return ripples;
}
